/**
 * Kitxens Knowledge Base — central FAQ aggregation.
 *
 * Design goals:
 *   1. Every service's FAQs are pulled automatically from serviceDetail.js.
 *      When a new service is added there, it flows into the FAQ page with
 *      zero extra plumbing — just tag its slug in SLUG_TO_CATEGORY below.
 *   2. Extra, non-service FAQs (about the company, AI team members, pricing,
 *      onboarding, support) live in EXTRA_FAQS and are grouped the same way.
 *   3. Structured for future upgrade to semantic / AI-powered search.
 */
import { SERVICE_DETAIL } from './serviceDetail';
import { SERVICE_CONTENT } from './serviceContent';

// The 6 top-level knowledge categories the site organizes around.
// Order matters: it drives the display order on /faq.
export const KB_CATEGORIES = [
  {
    id: 'general',
    label_en: 'General',
    label_es: 'General',
    description_en: 'What Kitxens is, who it is for, onboarding, contracts, support and the shape of the platform.',
    description_es: 'Qué es Kitxens, para quién es, onboarding, contratos, soporte y la forma de la plataforma.',
  },
  {
    id: 'it-pos',
    label_en: 'IT & POS Systems',
    label_es: 'IT y Sistemas POS',
    description_en: 'Toast, Clover, Square, Lightspeed, online ordering, integrations, hardware and 24/7 support.',
    description_es: 'Toast, Clover, Square, Lightspeed, pedidos online, integraciones, hardware y soporte 24/7.',
  },
  {
    id: 'digital-branding',
    label_en: 'Digital Branding',
    label_es: 'Branding Digital',
    description_en: 'Websites, Local SEO, Google Business Profile, reviews, reputation, photography and brand voice.',
    description_es: 'Webs, Local SEO, Google Business Profile, reseñas, reputación, fotografía y voz de marca.',
  },
  {
    id: 'ai-automation',
    label_en: 'AI & Automation',
    label_es: 'IA y Automatización',
    description_en: 'Index AI, Rachel, Stan, Eva, Sonny, Penny, Linda — how the AI operating team works and where it fits.',
    description_es: 'Index AI, Rachel, Stan, Eva, Sonny, Penny, Linda — cómo trabaja el equipo IA y dónde encaja.',
  },
  {
    id: 'revenue-growth',
    label_en: 'Revenue Growth',
    label_es: 'Crecimiento de Ingresos',
    description_en: 'Delivery, catering, customer marketing, loyalty, pricing, promotions and revenue analytics.',
    description_es: 'Delivery, catering, marketing, fidelización, pricing, promociones y analítica de ingresos.',
  },
  {
    id: 'data-analytics',
    label_en: 'Data & Analytics',
    label_es: 'Datos y Analítica',
    description_en: 'Dashboards, reporting, KPIs, forecasting and Restaurant Operating Intelligence.',
    description_es: 'Dashboards, reportes, KPIs, forecasting y Restaurant Operating Intelligence.',
  },
];

// Service slug -> category id.
// New services in serviceDetail.js MUST be tagged here to appear on /faq.
const SLUG_TO_CATEGORY = {
  // IT & POS
  'pos-management': 'it-pos',
  'pos-integrations': 'it-pos',
  'online-ordering': 'it-pos',
  // Digital Branding
  'restaurant-websites': 'digital-branding',
  'local-seo': 'digital-branding',
  'google-business-profile': 'digital-branding',
  'online-reputation': 'digital-branding',
  // Revenue Growth
  'delivery-optimization': 'revenue-growth',
  'catering-growth': 'revenue-growth',
  'customer-marketing': 'revenue-growth',
  // Data & Analytics
  'data-analytics': 'data-analytics',
  'performance-reporting': 'data-analytics',
  'business-intelligence': 'data-analytics',
  // AI & Automation
  'custom-ai-automation': 'ai-automation',
  'ai-receptionist': 'ai-automation',
  'review-automation': 'ai-automation',
  'catering-ai': 'ai-automation',
};

// Bilingual helper mirroring the one in serviceDetail.js
const b = (en, es) => ({ en, es });
const F = (q_en, a_en, q_es, a_es) => ({ q: b(q_en, q_es), a: b(a_en, a_es) });

/**
 * Extra FAQs that are NOT tied to a specific service page.
 * These populate the top of each category and cover the operator-level
 * questions that come up in sales conversations.
 * Grouped by category id.
 */
export const EXTRA_FAQS = {
  general: [
    F(
      'What exactly does Kitxens do?',
      'Kitxens is a single operating layer for restaurants. We deploy and support your POS, online ordering, AI automation, digital branding and analytics under one team, one phone number and one monthly subscription. We are not an agency — we are the platform that operates the connected stack a modern restaurant needs.',
      '¿Qué hace exactamente Kitxens?',
      'Kitxens es una capa operativa única para restaurantes. Implementamos y damos soporte a POS, pedidos online, automatización con IA, branding digital y analítica bajo un solo equipo, teléfono y suscripción mensual. No somos una agencia — somos la plataforma que opera el stack conectado que un restaurante moderno necesita.'
    ),
    F(
      'Who is Kitxens for?',
      'Independent restaurants, multi-location concepts and growing hospitality groups that want to consolidate vendors, reduce IT overhead and grow revenue without hiring an internal tech team.',
      '¿Para quién es Kitxens?',
      'Restaurantes independientes, conceptos multi-local y grupos de hospitalidad en crecimiento que quieren consolidar proveedores, reducir el overhead de IT y crecer sin contratar un equipo técnico interno.'
    ),
    F(
      'How is Kitxens different from a typical IT vendor or agency?',
      'Traditional vendors solve isolated tickets. Traditional agencies deliver monthly deliverables. Kitxens combines IT, POS, AI automation, marketing and analytics into one connected operating layer with shared data and shared accountability for business outcomes — not just uptime or clicks.',
      '¿En qué se diferencia Kitxens de un proveedor de IT o agencia típica?',
      'Los proveedores tradicionales resuelven tickets aislados. Las agencias entregan reportes mensuales. Kitxens combina IT, POS, IA, marketing y analítica en una capa operativa conectada con datos y responsabilidad compartida sobre resultados de negocio — no solo uptime o clics.'
    ),
    F(
      'How long does onboarding take?',
      'Most concepts are fully onboarded in 2 to 4 weeks depending on the number of locations and existing systems. Week 1 is audit and standard-setting; weeks 2–4 are activation. We handle the heavy lifting so your team keeps operating without disruption.',
      '¿Cuánto dura el onboarding?',
      'La mayoría de conceptos están listos en 2 a 4 semanas según los locales y sistemas existentes. Semana 1 es auditoría y estandarización; semanas 2–4 son activación. Nos encargamos del trabajo pesado para que tu equipo siga operando sin interrupciones.'
    ),
    F(
      'How is Kitxens priced?',
      'A flat monthly subscription based on the number of locations and modules you activate. No per-ticket fees, no surprise invoices. The free audit includes a tailored quote and a 12-month ROI projection.',
      '¿Cómo es el precio de Kitxens?',
      'Suscripción mensual fija según los locales y módulos que actives. Sin cargos por ticket, sin facturas sorpresa. La auditoría gratuita incluye cotización personalizada y proyección de ROI a 12 meses.'
    ),
    F(
      'Are there long-term contracts?',
      'No multi-year lock-ins. We offer month-to-month and annual options, with a 30-day notice cancellation policy. If we are not delivering, you leave.',
      '¿Hay contratos de largo plazo?',
      'No hay amarres multianuales. Ofrecemos opciones mes a mes y anuales, con cancelación con 30 días de aviso. Si no entregamos, te vas.'
    ),
    F(
      'Is the free audit really free?',
      'Yes. No obligation, no credit card, no pressure. A senior specialist reviews your current setup, benchmarks you against your named local competitors and shares the specific opportunities we see — whether you work with us or not.',
      '¿La auditoría gratuita es realmente gratis?',
      'Sí. Sin compromiso, sin tarjeta, sin presión. Un especialista senior revisa tu setup actual, te compara contra tus competidores locales y comparte las oportunidades específicas — trabajes con nosotros o no.'
    ),
    F(
      'Do you keep ownership of my accounts and data?',
      'You always own your accounts, your data and your customer relationships. We operate as manager-level access, revocable at any moment. Nothing transfers to us. Ever.',
      '¿Mantengo la propiedad de mis cuentas y datos?',
      'Siempre eres el dueño de tus cuentas, datos y relaciones con clientes. Operamos con acceso de manager, revocable en cualquier momento. Nada se nos transfiere. Nunca.'
    ),
    F(
      'Which markets does Kitxens operate in?',
      'We currently support restaurants across Europe, the US and Latin America, with a bilingual (English / Spanish) team and 24/7 operational coverage across timezones.',
      '¿En qué mercados opera Kitxens?',
      'Actualmente damos servicio en Europa, EE.UU. y Latinoamérica, con equipo bilingüe (inglés / español) y cobertura operativa 24/7 en todas las zonas horarias.'
    ),
    F(
      'How is support structured?',
      'Every account has a named human owner + AI operators on shift 24/7. Median first response under 60 seconds on WhatsApp, under 5 minutes on ticket, and a 4-hour SLA for anything critical.',
      '¿Cómo está estructurado el soporte?',
      'Cada cuenta tiene un dueño humano nombrado + operadores IA de turno 24/7. Primera respuesta mediana < 60s en WhatsApp, < 5 min en ticket, y SLA de 4h para lo crítico.'
    ),
  ],
  'ai-automation': [
    F(
      'Who are Rachel, Stan, Eva, Sonny, Penny and Linda?',
      'They are the six AI operators that make up the Kitxens AI Operating Team. Rachel handles guest conversations (concierge, reservations, WhatsApp). Stan prospects new catering accounts. Eva handles operations and internal workflows. Sonny handles social distribution. Penny handles SEO and content research. Linda handles contract and legal ops. Each is supervised by a human owner.',
      '¿Quiénes son Rachel, Stan, Eva, Sonny, Penny y Linda?',
      'Son los seis operadores IA del Kitxens AI Operating Team. Rachel gestiona conversaciones con clientes (concierge, reservas, WhatsApp). Stan prospecta nuevas cuentas de catering. Eva gestiona operaciones internas. Sonny gestiona distribución social. Penny hace research SEO y contenido. Linda gestiona contratos y legal. Cada uno supervisado por un dueño humano.'
    ),
    F(
      'Is Rachel really answering guests in real time?',
      'Yes. Rachel handles reservations, orders, catering inquiries and general questions across WhatsApp, Instagram DM, Google Business Messages and web, 24/7. Median response time in our portfolio is under 60 seconds. Complaints and safety incidents are escalated to a human immediately.',
      '¿Rachel realmente responde clientes en tiempo real?',
      'Sí. Rachel gestiona reservas, pedidos, catering y consultas generales en WhatsApp, Instagram DM, Google Business Messages y web, 24/7. Tiempo de respuesta mediano en nuestra cartera: < 60 segundos. Quejas e incidentes de seguridad se escalan a un humano de inmediato.'
    ),
    F(
      'Are the AI agents safe to put in front of guests?',
      'Yes. Every agent is calibrated on your menu, brand voice and policies, trained on your specific escalation rules, and gated with clear handoffs to humans for sensitive situations. For the first 90 days every complaint response is human-approved before sending.',
      '¿Es seguro poner los agentes IA frente a clientes?',
      'Sí. Cada agente está calibrado con tu menú, voz de marca y políticas, entrenado en tus reglas de escalado, y con handoff claro a humanos en situaciones sensibles. Los primeros 90 días cada respuesta a queja se aprueba humanamente antes de enviarse.'
    ),
    F(
      'What is Index AI, exactly?',
      'Index AI is the layer that continuously keeps your restaurant "citable" by Google AI Overviews, ChatGPT, Perplexity and Apple Intelligence. It updates structured data, monitors coherence across surfaces, drafts posts, responds to Q&A and reports citation share against your named competitors.',
      '¿Qué es Index AI exactamente?',
      'Index AI es la capa que mantiene tu restaurante "citable" por Google AI Overviews, ChatGPT, Perplexity y Apple Intelligence. Actualiza datos estructurados, monitoriza coherencia entre superficies, redacta posts, responde Q&A y reporta share de citación vs. tus competidores nombrados.'
    ),
    F(
      'What can an AI agent NOT do at Kitxens?',
      'Never: issue a refund above the human threshold, respond to press, negotiate contracts, touch any guest safety incident, or use a voice that has not been approved by the operating owner. Those boundaries are non-negotiable. That is what earns them the trust to own the other 95%.',
      '¿Qué NO puede hacer un agente IA en Kitxens?',
      'Nunca: emitir un reembolso sobre el umbral humano, responder prensa, negociar contratos, tocar un incidente de seguridad del cliente, ni usar una voz no aprobada por el dueño operativo. Esos límites no son negociables. Eso es lo que les gana la confianza para operar el otro 95%.'
    ),
    F(
      'Can the agents integrate with my existing POS and booking system?',
      'Yes. We integrate with Toast, Clover, Square, Lightspeed, SpotOn on the POS side, and OpenTable, The Fork, SevenRooms, Resy on the booking side. The agents can read and write to real data.',
      '¿Los agentes se integran con mi POS y sistema de reservas?',
      'Sí. Integramos con Toast, Clover, Square, Lightspeed, SpotOn en POS, y OpenTable, The Fork, SevenRooms, Resy en reservas. Los agentes pueden leer y escribir en datos reales.'
    ),
    F(
      'How is the AI trained on our brand voice?',
      'We build a "voice contract" with you in week one: pronouns, opener/closer, banned phrases, tone, humor handling. It is under 400 words, reviewed monthly the first quarter, then quarterly. Every AI response is filtered through it.',
      '¿Cómo se entrena la IA con la voz de nuestra marca?',
      'Creamos un "contrato de voz" contigo en semana 1: pronombres, apertura/cierre, frases prohibidas, tono, humor. Menos de 400 palabras, revisado mensualmente el primer trimestre, luego trimestralmente. Cada respuesta IA pasa por él.'
    ),
    F(
      'How quickly will we see results from the AI agents?',
      'Most restaurants see immediate impact within 14 days: response time collapses to under 60s, review response coverage lifts above 95%, catering inquiries close 2x faster. Full compounding takes 60–90 days.',
      '¿Cuán rápido veremos resultados de los agentes IA?',
      'La mayoría ve impacto en 14 días: tiempo de respuesta baja a < 60s, cobertura de respuesta a reseñas sube > 95%, catering cierra 2x más rápido. El efecto compuesto pleno toma 60–90 días.'
    ),
  ],
  'digital-branding': [
    F(
      'Do you build restaurant websites from scratch?',
      'Yes. And we optimize them for AI search from day one — structured data (Restaurant, Menu, MenuItem, FAQ schema), page-speed under 2s on mobile, direct-order integration and mobile-first checkout. Not just a brochure.',
      '¿Construyen webs de restaurante desde cero?',
      'Sí. Y las optimizamos para búsqueda IA desde el día 1 — datos estructurados (Restaurant, Menu, MenuItem, FAQ), page-speed < 2s en móvil, integración de pedidos y checkout mobile-first. No un folleto.'
    ),
    F(
      'What is "AI search citation" and why should I care?',
      'When a diner asks ChatGPT or Google "where should I eat tonight?", the model composes a paragraph mentioning 2–3 restaurants. If yours is not one of them, you lose the visit without ever seeing it on analytics. Our Index AI + Local AI layers are built to compound your share of these citations.',
      '¿Qué es "citación en búsqueda IA" y por qué importa?',
      'Cuando un comensal pregunta a ChatGPT o Google "¿dónde ceno hoy?", el modelo compone un párrafo mencionando 2–3 restaurantes. Si no eres uno, pierdes la visita sin verlo en analytics. Nuestras capas Index AI + Local AI compone tu participación de citaciones.'
    ),
    F(
      'How is Kitxens photography different from a standard shoot?',
      'We shoot monthly, on a rotating variety schedule (dish / room / team / exterior / golden-hour), specifically for the AI layer\'s "freshness" signal and the direct-order channel\'s conversion — not for a portfolio.',
      '¿En qué se diferencia la fotografía Kitxens de una sesión estándar?',
      'Fotografiamos mensualmente, con variedad rotativa (plato / sala / equipo / exterior / golden-hour), específicamente para la señal de "frescura" que lee la capa IA y la conversión del canal directo — no para un portfolio.'
    ),
  ],
  'revenue-growth': [
    F(
      'What is direct ordering and why is it more important than delivery marketplaces?',
      'Direct ordering means the diner orders through YOUR site — not Uber Eats or DoorDash. Marketplace commissions are 25–35% of the ticket. Direct is typically 2–4% payment processing. Same ticket, 20–30 more points of margin. In our portfolio, direct is between 22% and 60% of total online orders.',
      '¿Qué es pedido directo y por qué importa más que las apps de delivery?',
      'Pedido directo significa que el comensal pide en TU web — no Uber Eats ni DoorDash. Las comisiones de marketplace son 25–35% del ticket. Directo suele ser 2–4% de procesamiento. Mismo ticket, 20–30 puntos más de margen. En nuestra cartera, directo es entre 22% y 60% de los pedidos online totales.'
    ),
    F(
      'Can Kitxens really reduce our no-show rate?',
      'Yes. Our 4-layer system (confirmation cadence + hold policy + deposits on high-stakes + weekly post-mortem) moves no-show rates from the industry-average 10–14% down to 3–6% within 60 days. Full write-up in the Magazine.',
      '¿Kitxens puede realmente reducir nuestros no-shows?',
      'Sí. Nuestro sistema de 4 capas (cadencia de confirmación + política de retención + depósitos + post-mortem semanal) baja los no-shows del 10–14% habitual a 3–6% en 60 días. Deep-dive en el Magazine.'
    ),
    F(
      'Should my restaurant use dynamic pricing?',
      'Sometimes. Time-of-day and day-of-week pricing work almost universally. Demand-based pricing on regular menu items backfires in most markets, especially southern Europe. Tasting menus, private rooms and channel-specific pricing generally work. We audit and recommend by venue.',
      '¿Debería mi restaurante usar pricing dinámico?',
      'A veces. Precios por hora y día de la semana funcionan casi siempre. Pricing por demanda en menú regular sale mal en la mayoría de mercados, sobre todo en el sur de Europa. Tasting menus, salas privadas y precios por canal generalmente funcionan. Auditamos y recomendamos por local.'
    ),
    F(
      'How do you measure marketing effectiveness?',
      'Contribution margin per euro/dollar spent, per channel, tracked end-to-end from first touch to confirmed booking. Not clicks. Not impressions. Not view-through. Anything below 2x contribution-to-spend gets cut. Anything above 4x gets scaled.',
      '¿Cómo miden la efectividad del marketing?',
      'Margen de contribución por euro/dólar gastado, por canal, trackeado end-to-end desde primer contacto hasta reserva confirmada. No clics. No impresiones. No view-through. Por debajo de 2x se corta. Por encima de 4x se escala.'
    ),
    F(
      'Do you help with catering sales?',
      'Yes. Our Cater AI + Stan combination surfaces new corporate accounts in your delivery radius, qualifies inbound catering inquiries in minutes, and drafts proposals in your voice. Catering close rate in our portfolio typically 2x in the first quarter.',
      '¿Ayudan con ventas de catering?',
      'Sí. La combinación Cater AI + Stan descubre nuevas cuentas corporativas en tu radio de reparto, califica consultas de catering en minutos, y redacta propuestas en tu voz. La tasa de cierre suele duplicarse en el primer trimestre.'
    ),
  ],
  'data-analytics': [
    F(
      'What is Restaurant Operating Intelligence?',
      'It is the connected view of every signal your restaurant generates — POS, direct orders, reservations, delivery, marketing, reviews, AI search citations — surfaced in real time to the operating owner. Not a monthly report. A live operating layer.',
      '¿Qué es Restaurant Operating Intelligence?',
      'Es la vista conectada de cada señal que genera tu restaurante — POS, pedidos directos, reservas, delivery, marketing, reseñas, citaciones de IA — surfaceada en tiempo real al dueño operativo. No un reporte mensual. Una capa operativa viva.'
    ),
    F(
      'Which KPIs do you actually track?',
      'Contribution margin per channel, no-show rate, direct-order share of online, 90-day repeat rate, review response coverage, AI-search citation share, median first-response time, catering close rate, cost-per-cover by daypart. The five that decide the P&L.',
      '¿Qué KPIs realmente rastrean?',
      'Margen de contribución por canal, tasa de no-show, share directo del online, repeat rate a 90 días, cobertura de respuesta a reseñas, share de citación IA, tiempo mediano de primera respuesta, tasa de cierre en catering, coste por cover por franja. Los cinco que deciden el P&L.'
    ),
    F(
      'Do you replace our accountant or bookkeeper?',
      'No. We surface the operating numbers — the ones that drive daily decisions. Your accountant handles the financial statements. Our dashboards feed them cleaner, faster data.',
      '¿Reemplazan a nuestro contable?',
      'No. Nosotros surfaceamos los números operativos — los que dirigen decisiones diarias. Tu contable gestiona los estados financieros. Nuestros dashboards les entregan datos más limpios y rápidos.'
    ),
    F(
      'Do you forecast demand?',
      'Yes. Weekly cover forecast per daypart per location, based on historical patterns, weather, local events and marketing spend. Accuracy inside 8% in mature installations.',
      '¿Hacen forecasting de demanda?',
      'Sí. Forecast semanal de covers por franja por local, basado en patrones históricos, clima, eventos locales y gasto en marketing. Precisión dentro del 8% en instalaciones maduras.'
    ),
  ],
  'it-pos': [
    F(
      'What is a "cloud-based IT & POS department"?',
      'It means the IT and POS work traditionally handled by an in-house team (installation, configuration, updates, integrations, troubleshooting, 24/7 support) is delivered by Kitxens as a managed layer — same result, none of the headcount.',
      '¿Qué es un "departamento IT & POS en la nube"?',
      'Significa que el trabajo IT y POS que tradicionalmente hace un equipo interno (instalación, configuración, updates, integraciones, troubleshooting, soporte 24/7) lo entregamos como capa gestionada — mismo resultado, sin la plantilla.'
    ),
    F(
      'What if we already have a POS we like?',
      'We keep it. Kitxens does not force platform changes. If your POS works for you, we operate and integrate around it. If it does not, we recommend and lead the migration with zero downtime.',
      '¿Qué pasa si ya tenemos un POS que nos gusta?',
      'Lo mantenemos. Kitxens no fuerza cambios de plataforma. Si tu POS te sirve, operamos e integramos alrededor. Si no, recomendamos y lideramos la migración sin downtime.'
    ),
    F(
      'Can you migrate us from one POS to another?',
      'Yes. We handle full POS migrations end-to-end — data export, menu translation, hardware swap, staff training, cutover — with zero missed service. Typical migration timeline is 2–3 weeks.',
      '¿Pueden migrarnos de un POS a otro?',
      'Sí. Gestionamos migraciones POS end-to-end — export de datos, traducción de menú, cambio de hardware, formación de equipo, cutover — sin perder servicio. Timeline típico 2–3 semanas.'
    ),
  ],
};

/**
 * Build the flat list of all knowledge entries.
 * Every entry has: { category, source, sourceHref, sourceLabel, q, a, id }
 *   - source: 'service' or 'extra'
 *   - sourceHref: link back to the origin page for provenance / SEO
 *   - sourceLabel: human-friendly name of the origin service
 */
export function buildKnowledgeBase() {
  const entries = [];

  // 1) Extra (non-service) FAQs first — keep them at the top of each category.
  Object.entries(EXTRA_FAQS).forEach(([catId, faqs]) => {
    faqs.forEach((f, i) => {
      entries.push({
        id: `extra-${catId}-${i}`,
        category: catId,
        source: 'extra',
        sourceHref: '/faq',
        sourceLabel_en: 'Kitxens',
        sourceLabel_es: 'Kitxens',
        q: f.q,
        a: f.a,
      });
    });
  });

  // 2) Every service's FAQs, mapped by slug -> category.
  Object.entries(SERVICE_DETAIL).forEach(([slug, detail]) => {
    const cat = SLUG_TO_CATEGORY[slug];
    if (!cat || !detail?.faqs) return;
    const service = SERVICE_CONTENT?.[slug];
    const label_en = service?.en?.title || slug;
    const label_es = service?.es?.title || label_en;
    // Detect prefix ('services' vs 'ai') from the service content (best-effort).
    const isAi = !!service?.ai;
    const prefix = isAi ? 'ai' : 'services';
    detail.faqs.forEach((f, i) => {
      entries.push({
        id: `${slug}-${i}`,
        category: cat,
        source: 'service',
        sourceHref: `/${prefix}/${slug}`,
        sourceLabel_en: label_en,
        sourceLabel_es: label_es,
        q: f.q,
        a: f.a,
      });
    });
  });

  return entries;
}

/**
 * Group knowledge entries by category, respecting KB_CATEGORIES order.
 * Returns [{category, entries: [...]}] with only non-empty categories.
 */
export function groupByCategory(entries) {
  const map = new Map();
  KB_CATEGORIES.forEach((c) => map.set(c.id, []));
  entries.forEach((e) => {
    if (!map.has(e.category)) map.set(e.category, []);
    map.get(e.category).push(e);
  });
  return KB_CATEGORIES
    .map((c) => ({ category: c, entries: map.get(c.id) || [] }))
    .filter((g) => g.entries.length > 0);
}
